import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  Grid,
  Divider,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import KumocanButton from 'components/button'
import KumocanDialog from 'components/dialog'
import { notificationApi } from 'hooks/api/notification'
import { PreferredTeamSize, PreferredWorksite } from 'types/security'

const worksiteKeys = [
  { key: 'road_construction', value: PreferredWorksite.RoadConstruction },
  { key: 'parking_lot', value: PreferredWorksite.ParkingLot },
  { key: 'building_site', value: PreferredWorksite.BuildingSite },
  { key: 'other_construction', value: PreferredWorksite.OtherConstruction },
] as const

export default function InterviewsPage() {
  const { t } = useTranslation('page-notification-interviews')
  const { id } = useParams<{ id: string }>()
  const [preferredTeamSize, setPreferredTeamSize] = useState<number>(0)
  const [preferredSites, setPreferredSites] = useState<number[]>([])
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    if (id) {
      notificationApi().readed(id)
      notificationApi()
        .findInteview(id)
        .then((res) => {
          if (res.data.preferredTeamSizes.length) {
            setPreferredTeamSize(res.data.preferredTeamSizes[0])
          }
          if (res.data.preferredWorkSites.length) {
            setPreferredSites(res.data.preferredWorkSites)
          }
        })
    }
  }, [id])

  const saveValue = async () => {
    return await notificationApi().updateInteview(
      id!,
      [preferredTeamSize],
      preferredSites,
    )
  }

  const handleTeamSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferredTeamSize(Number(event.target.value))
    setTimeout(() => {
      saveValue()
    }, 500)
  }

  const handleSiteToggle = (site: number) => {
    setPreferredSites((prev) =>
      prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site],
    )
    setTimeout(() => {
      saveValue()
    }, 500)
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    saveValue().then(() => {
      setOpen(true)
    })
  }

  const toTop = () => {
    location.href = '/notification'
  }

  const disabled = !preferredTeamSize && preferredSites.length === 0

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h2" gutterBottom>
        {t('title')}
      </Typography>
      <Box sx={{ backgroundColor: '#ffa895', padding: '10px' }}>
        {t('notice')}
      </Box>
      <Paper sx={{ p: 2, mt: 1 }}>
        <form onSubmit={handleSubmit}>
          {/* 質問1: ラジオボタン */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Typography component="strong" sx={{ fontSize: '1.2rem' }}>
              {t('question_team_size')}
              <br />
              {t('single_select')}
            </Typography>
            <RadioGroup
              name="teamSize"
              value={preferredTeamSize}
              onChange={handleTeamSizeChange}
            >
              <FormControlLabel
                value={PreferredTeamSize.OnePerson}
                control={
                  <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />
                }
                label={
                  <Typography sx={{ fontSize: '1.4rem' }}>
                    {t('team_size.one')}
                  </Typography>
                }
              />
              <FormControlLabel
                value={PreferredTeamSize.TwoToFour}
                control={
                  <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />
                }
                label={
                  <Typography sx={{ fontSize: '1.4rem' }}>
                    {t('team_size.two_to_four')}
                  </Typography>
                }
              />
              <FormControlLabel
                value={PreferredTeamSize.FiveOrMore}
                control={
                  <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />
                }
                label={
                  <Typography sx={{ fontSize: '1.4rem' }}>
                    {t('team_size.five_or_more')}
                  </Typography>
                }
              />
              <FormControlLabel
                value={PreferredTeamSize.DontCare}
                control={
                  <Radio sx={{ '& .MuiSvgIcon-root': { fontSize: '2rem' } }} />
                }
                label={
                  <Typography sx={{ fontSize: '1.4rem' }}>
                    {t('team_size.dont_care')}
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          {/* 質問2 */}
          <Divider />
          <FormControl component="fieldset" sx={{ marginTop: '20px' }}>
            <Typography component="legend" sx={{ fontSize: '1.2rem' }}>
              {t('question_worksite')}
              <br />
              {t('multi_select')}
            </Typography>
            <Grid container spacing={2} textAlign="left" marginTop={'7px'}>
              {worksiteKeys.map((option) => (
                <Grid item xs={12} key={option.value} marginLeft="10px">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="large"
                        checked={preferredSites.includes(Number(option.value))}
                        onChange={() => handleSiteToggle(Number(option.value))}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '1.4rem' }}>
                        {t(`worksite.${option.key}`)}
                      </Typography>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </FormControl>
          <Button
            disabled={disabled}
            type="submit"
            variant="contained"
            color="primary"
            sx={{ fontSize: '1.3rem', minWidth: '200px', marginTop: '30px' }}
          >
            {t('submit')}
          </Button>
        </form>
      </Paper>
      <KumocanDialog open={open}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography fontSize={'18px'} textAlign={'center'}>
            {t('thank_you_title')}
            <br />
            {t('thank_you_message')}
          </Typography>
          <KumocanButton
            onClick={toTop}
            style={{
              margin: '20px auto 0',
              minWidth: '120px',
              fontSize: '18px',
            }}
          >
            {t('back_to_list')}
          </KumocanButton>
        </Box>
      </KumocanDialog>
    </Box>
  )
}
